import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  Divider,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import dayjs from 'dayjs';
import { type ClipboardEvent, type DragEvent, useMemo, useRef, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { QmAttachment, QmCrudStatus, QmRecord } from '../../api/qmCrud';
import { downloadUrlAsFile } from '../../common/download';
import { QM_MAX_ATTACHMENT_SIZE_BYTES } from '../../common/constants';
import { ATTACHMENT_ACCEPT_TYPES, isSupportedAttachment, normalizeAttachmentFiles } from '../../common/qmAttachments';
import { StyledButton } from '../../components';
import { useAlert, usePhasedModelGate } from '../../hooks';
import useObserveTripsheet from '../../hooks/useObserveTripsheet';
import QmHistoryTimeline from './QmHistoryTimeline';

export type OptionItem = {
  code: string;
  label: string;
};

const SITE_INBOUND_RECEIVING_WAREHOUSE_FOOTER_REASON_CODES = new Set([
  'SITE_INBOUND_CYCLE_COUNT_IN_PROGRESS',
  'SITE_INBOUND_WAREHOUSE_LOCATION_NOT_ACTIVE',
  'SITE_INBOUND_OTHER_LOGGED_ISSUE'
]);
const SITE_INBOUND_SYSTEM_GENERATED_REASON_CODES = SITE_INBOUND_RECEIVING_WAREHOUSE_FOOTER_REASON_CODES;
const SITE_INBOUND_SSCC_REASON_CODES = new Set([
  'SITE_INBOUND_SSCC_NOT_ON_MANIFEST',
  'SITE_INBOUND_SSCC_NOT_DELIVERED',
  'SITE_INBOUND_DAMAGED_IN_TRANSIT',
  'SITE_INBOUND_NOT_FIT_FOR_TRANSPORT'
]);
const SITE_INBOUND_ASN_LINE_REASON_CODES = new Set([
  'SITE_INBOUND_ASN_NOT_ON_MANIFEST',
  'SITE_INBOUND_ASN_NOT_DELIVERED',
  'SITE_INBOUND_INCORRECT_GOODS',
  'SITE_INBOUND_INCORRECT_QTY'
]);

export type QmDetailDialogModel = {
  selectedQm: QmRecord | null;
  isDetailsLoading: boolean;
  canEdit: boolean;
  canUploadAttachments: boolean;
  canReprintLabels: boolean;
  showFilteredOutNotice: boolean;
  sectionCardSx: Record<string, unknown>;
  attachments: QmAttachment[];
  isAttachmentsLoading: boolean;
  isUploadingAttachments: boolean;
  onUploadAttachments: (files: File[]) => void;
  onSelectPreview: (preview: { name: string; url: string }) => void;
  actionComment: string;
  isAddingActionComment: boolean;
  onActionCommentChange: (value: string) => void;
  addActionComment: () => void;
  onReprintLabels: () => void;
  isReprintingLabels: boolean;
  editStatus: QmCrudStatus;
  onEditStatusChange: (status: QmCrudStatus) => void;
  editReasonCode: string;
  onEditReasonCodeChange: (value: string) => void;
  editSupplierName: string;
  onEditSupplierNameChange: (value: string) => void;
  editContactName: string;
  onEditContactNameChange: (value: string) => void;
  editSiteInboundSiteId?: string;
  onEditSiteInboundSiteIdChange?: (value: string) => void;
  editItemLocation: string;
  onEditItemLocationChange: (value: string) => void;
  editOtherInformation: string;
  onEditOtherInformationChange: (value: string) => void;
  editDescription: string;
  onEditDescriptionChange: (value: string) => void;
  editClosureCode: string;
  onEditClosureCodeChange: (value: string) => void;
  editClosureNotes: string;
  onEditClosureNotesChange: (value: string) => void;
  saveQmStatusEdits: () => Promise<boolean>;
  saveQmDetailEdits: () => Promise<boolean>;
  closureOptions: OptionItem[];
  reasonOptions: OptionItem[];
  editReasonOptions: OptionItem[];
  siteInboundSiteOptions?: OptionItem[];
  locationOptions: OptionItem[];
  getLocationLabel: (itemLocation: string | null | undefined) => string;
  getQmTypeLabel: (type: string | null | undefined, typeLabel?: string | null | undefined) => string;
  statusOptions: QmCrudStatus[];
  formatStatusLabel: (status: QmCrudStatus | string | null | undefined) => string;
  siteInboundCoordinatorAssignmentLabel?: string | null;
  onOpenInQmCases?: () => void;
  onCloseForNavigation?: () => void;
};

type QmDetailDialogProps = {
  open: boolean;
  onClose: () => void;
  model: QmDetailDialogModel;
};

function formatAttachmentSize(sizeBytes: number | null): string {
  if (!sizeBytes || sizeBytes <= 0) return '-';
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatAsnLineDetail(line: QmRecord['poLines'][number]): string {
  const payload = line.linePayload || {};
  const sourcePartId = String(payload.sourcePartId || payload.originalPartId || payload.original_part_id || '').trim();
  const description = String(payload.description || '').trim();
  const received = line.receivedQuantity ?? (payload.quantityReceived as number | null | undefined);
  const receivedValue = received === null || received === undefined ? '' : String(received);
  const parts = [sourcePartId, description, receivedValue ? `Received: ${receivedValue}` : ''].filter(Boolean);
  return parts.join(' - ') || String(line.poLineId || line.poLine || '').trim() || '-';
}

function formatDescription(description: string | null | undefined) {
  if (!description) return '-';
  const lines = description.split('\n');
  return (
    <>
      {lines.map((line, i) => (
        <span key={i}>
          {line.toLowerCase().startsWith('transaction type:') ||
          line.toLowerCase().startsWith('coupa transaction type:')
            ? (() => {
                const idx = line.indexOf(':');
                return (
                  <span>
                    <strong>{line.slice(0, idx + 1)}</strong>
                    <strong style={{ color: '#1976d2' }}>{line.slice(idx + 1)}</strong>
                  </span>
                );
              })()
            : line}
          {i < lines.length - 1 ? '\n' : ''}
        </span>
      ))}
    </>
  );
}

async function downloadAttachmentWithOriginalFileName(attachment: QmAttachment): Promise<void> {
  await downloadUrlAsFile(attachment.downloadUrl, attachment.fileName, {
    fallbackToNewTab: true
  });
}

export default function QmDetailDialog({ open, onClose, model }: QmDetailDialogProps) {
  const theme = useTheme();
  const alert = useAlert();
  const navigate = useNavigate();

  const {
    selectedQm,
    isDetailsLoading,
    canEdit,
    canUploadAttachments,
    canReprintLabels,
    showFilteredOutNotice,
    sectionCardSx,
    attachments,
    isAttachmentsLoading,
    isUploadingAttachments,
    onUploadAttachments,
    onSelectPreview,
    actionComment,
    isAddingActionComment,
    onActionCommentChange,
    addActionComment,
    onReprintLabels,
    isReprintingLabels,
    editStatus,
    onEditStatusChange,
    editReasonCode,
    onEditReasonCodeChange,
    editSupplierName,
    onEditSupplierNameChange,
    editContactName,
    onEditContactNameChange,
    editSiteInboundSiteId = '',
    onEditSiteInboundSiteIdChange,
    editItemLocation,
    onEditItemLocationChange,
    editOtherInformation,
    onEditOtherInformationChange,
    editDescription,
    onEditDescriptionChange,
    editClosureCode,
    onEditClosureCodeChange,
    editClosureNotes,
    onEditClosureNotesChange,
    saveQmStatusEdits,
    saveQmDetailEdits,
    closureOptions,
    reasonOptions,
    editReasonOptions,
    siteInboundSiteOptions = [],
    locationOptions,
    getLocationLabel,
    getQmTypeLabel,
    statusOptions,
    formatStatusLabel,
    siteInboundCoordinatorAssignmentLabel,
    onOpenInQmCases,
    onCloseForNavigation
  } = model;

  const linkedTripsheet = useObserveTripsheet(selectedQm?.tripId);
  const { loading: isTripSheetDataLoading } = usePhasedModelGate('TripSheet');
  const isLinkedTripsheetStillSyncing = Boolean(selectedQm?.manifestId && !linkedTripsheet && isTripSheetDataLoading);
  const receivingWarehouseFooterLabel = useMemo(() => {
    if (!selectedQm || selectedQm.type !== 'SITE_INBOUND') return null;
    if (!SITE_INBOUND_RECEIVING_WAREHOUSE_FOOTER_REASON_CODES.has(selectedQm.reasonCode)) return null;

    const receivingWarehouseId = String(selectedQm.receivingWarehouseId || '').trim();
    if (!receivingWarehouseId) return null;

    const warehouseLabel = String(selectedQm.receivingWarehouseLabel || '').trim();
    return warehouseLabel ? `${warehouseLabel} (${receivingWarehouseId})` : receivingWarehouseId;
  }, [selectedQm]);

  const [isUploadConfirmOpen, setIsUploadConfirmOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [isSavingDetail, setIsSavingDetail] = useState(false);
  const [pendingUploadFiles, setPendingUploadFiles] = useState<File[]>([]);
  const [isAttachmentDragActive, setIsAttachmentDragActive] = useState(false);
  const attachmentDragDepthRef = useRef(0);

  const imageAttachments = useMemo(
    () => attachments.filter((attachment) => (attachment.mimeType || '').toLowerCase().startsWith('image/')),
    [attachments]
  );

  const fileAttachments = useMemo(
    () => attachments.filter((attachment) => !(attachment.mimeType || '').toLowerCase().startsWith('image/')),
    [attachments]
  );

  const isClosedRecord = selectedQm?.status === 'CLOSED';
  const hasSiteInboundWave2Context = Boolean(
    selectedQm?.type === 'SITE_INBOUND' &&
    (selectedQm.itemLocationId ||
      selectedQm.tripId ||
      selectedQm.tripReference ||
      selectedQm.manifestId ||
      (selectedQm.poLines || []).some((line) => Boolean(line.linePayload?.asn)) ||
      (selectedQm.packages || []).length > 0)
  );
  const canEditWorkflowFields = canEdit && selectedQm?.status !== 'CLOSED';
  const isSystemGeneratedSiteInboundQm = Boolean(
    selectedQm?.type === 'SITE_INBOUND' && SITE_INBOUND_SYSTEM_GENERATED_REASON_CODES.has(selectedQm.reasonCode)
  );
  const canEditDetailFields =
    canUploadAttachments && selectedQm?.status !== 'CLOSED' && !isSystemGeneratedSiteInboundQm;
  const canEditReason = canEditDetailFields && selectedQm?.status === 'CREATED';
  const canEditItemLocation = canEditDetailFields && selectedQm?.status === 'CREATED';
  const shouldShowClosureFields = selectedQm?.status === 'CLOSED' || editStatus === 'CLOSED';
  const canEditClosureFields = canEdit && selectedQm?.status !== 'CLOSED' && editStatus === 'CLOSED';

  const poLineDisplayValue = useMemo(() => {
    if (!selectedQm) return '-';

    const values = (selectedQm.poLines || [])
      .map((line) => String(line.poLine || '').trim())
      .filter((value) => value.length > 0);

    if (values.length > 0) return values.join('\n');
    return selectedQm.poLine || '-';
  }, [selectedQm]);

  const poLineIdDisplayValue = useMemo(() => {
    if (!selectedQm) return '-';

    const values = (selectedQm.poLines || [])
      .map((line) => String(line.poLineId || '').trim())
      .filter((value) => value.length > 0);

    if (values.length > 0) return values.join(', ');
    return selectedQm.poLineId || '-';
  }, [selectedQm]);

  const asnLineDisplayValue = useMemo(() => {
    if (!selectedQm) return '-';

    const values = (selectedQm.poLines || []).map(formatAsnLineDetail).filter((value) => value !== '-');
    if (values.length > 0) return values.join('\n');
    return selectedQm.poLineId || selectedQm.poLine || '-';
  }, [selectedQm]);

  const itemIdDisplayValue = useMemo(() => {
    if (!selectedQm) return '-';

    const values = (selectedQm.poLines || [])
      .map((line) => String(line.itemId || '').trim())
      .filter((value) => value.length > 0);

    if (values.length > 0) return values.join(', ');
    return selectedQm.itemId || '-';
  }, [selectedQm]);

  const packageCountDisplayValue = useMemo(() => {
    if (!selectedQm) return '-';
    if ((selectedQm.packages || []).length > 0) return String(selectedQm.packages.length);
    return selectedQm.packageCount?.toString() || '-';
  }, [selectedQm]);

  const packageTypeDisplayValue = useMemo(() => {
    if (!selectedQm) return '-';

    const values = (selectedQm.packages || [])
      .sort((a, b) => a.packageNumber - b.packageNumber)
      .map((pkg) => {
        const typeValue = String(pkg.packageType || '').trim() || '-';
        return `#${pkg.packageNumber}: ${typeValue}`;
      });

    if (values.length > 0) return values.join(', ');
    return selectedQm.packageType || '-';
  }, [selectedQm]);

  const itemLocationDisplayValue = useMemo(() => {
    if (!selectedQm) return '-';
    if (selectedQm.type === 'SITE_INBOUND' && selectedQm.itemLocationId) {
      return String(selectedQm.itemLocation || '').trim() || '-';
    }
    return getLocationLabel(selectedQm.itemLocation);
  }, [getLocationLabel, selectedQm]);

  const locationSelectOptions = useMemo(() => {
    const value = String(editItemLocation || '').trim();
    if (!value) return locationOptions;

    const normalized = value.toLowerCase();
    const exists = locationOptions.some(
      (item) => item.code.trim().toLowerCase() === normalized || item.label.trim().toLowerCase() === normalized
    );

    if (exists) return locationOptions;

    const fallbackLabel =
      selectedQm?.type === 'SITE_INBOUND' && selectedQm.itemLocationId && value === String(selectedQm.itemLocationId)
        ? `${selectedQm.itemLocation || value} (archived)`
        : `${value} (archived)`;

    return [...locationOptions, { code: value, label: fallbackLabel }];
  }, [editItemLocation, locationOptions, selectedQm]);

  const filteredEditReasonOptions = useMemo(() => {
    if (!selectedQm || selectedQm.type !== 'SITE_INBOUND') {
      return editReasonOptions;
    }

    const currentReasonCode = selectedQm.reasonCode;
    const isCurrentSsccReason = SITE_INBOUND_SSCC_REASON_CODES.has(currentReasonCode);
    const isCurrentAsnLineReason = SITE_INBOUND_ASN_LINE_REASON_CODES.has(currentReasonCode);

    if (!isCurrentSsccReason && !isCurrentAsnLineReason) {
      return editReasonOptions.filter((item) => item.code === currentReasonCode);
    }

    const allowedReasonCodes = isCurrentSsccReason
      ? SITE_INBOUND_SSCC_REASON_CODES
      : SITE_INBOUND_ASN_LINE_REASON_CODES;
    const filteredOptions = editReasonOptions.filter((item) => allowedReasonCodes.has(item.code));

    if (filteredOptions.some((item) => item.code === currentReasonCode)) {
      return filteredOptions;
    }

    return [
      {
        code: currentReasonCode,
        label: selectedQm.reasonLabel || currentReasonCode
      },
      ...filteredOptions
    ];
  }, [editReasonOptions, selectedQm]);

  const receiveManifestNavigationContext = useMemo(() => {
    if (!selectedQm) {
      return {
        tripId: '',
        legStatus: '',
        legDestination: '',
        tripReference: ''
      };
    }

    const manifestId = String(selectedQm.manifestId || '').trim();
    const matchedLeg = linkedTripsheet?.legs.find((leg) =>
      leg.manifests.some((manifest) => String(manifest.id || '').trim() === manifestId)
    );

    return {
      tripId: String(selectedQm.tripId || linkedTripsheet?.id || '').trim(),
      legStatus: String(matchedLeg?.status || '').trim(),
      legDestination: String(matchedLeg?.destination?.businessName || '').trim(),
      tripReference: String(selectedQm.tripReference || linkedTripsheet?.reference || '').trim()
    };
  }, [linkedTripsheet, selectedQm]);

  const renderPurchaseOrderValue = (poValue: string | null | undefined) => {
    const poNumbers = Array.from(
      new Set(
        String(poValue || '')
          .split(/[,\s]+/)
          .map((po) => po.trim())
          .filter(Boolean)
      )
    );

    if (poNumbers.length === 0) return '-';
    if (!process.env.REACT_APP_COUPA_BASE_URL) return poNumbers.join(', ');

    return (
      <Box
        component="span"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: 0.45,
          rowGap: 0.15,
          lineHeight: 1.35
        }}>
        {poNumbers.map((poNumber, index) => {
          const coupaPoUrl = `${process.env.REACT_APP_COUPA_BASE_URL}${poNumber}`;

          return (
            <Box key={poNumber} component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <MuiLink
                component="a"
                href={coupaPoUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.35,
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  lineHeight: 1.3,
                  color: 'primary.main',
                  textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                  '&:hover': {
                    color: 'primary.dark'
                  }
                }}>
                {poNumber}
                <OpenInNewIcon sx={{ fontSize: 12 }} />
              </MuiLink>
              {index < poNumbers.length - 1 ? <Box component="span">,</Box> : null}
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderManifestValue = (qm: QmRecord) => {
    const manifestId = String(qm.manifestId || '').trim();
    if (!manifestId) return '-';

    if (isLinkedTripsheetStillSyncing) {
      return (
        <Stack spacing={0.25}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary' }}>{manifestId}</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontStyle: 'italic' }}>
            Trip sheet still loading…
          </Typography>
        </Stack>
      );
    }

    const params = new URLSearchParams();
    const { tripId, legStatus, legDestination, tripReference } = receiveManifestNavigationContext;
    if (tripId) params.set('tripId', tripId);
    if (legStatus) params.set('legStatus', legStatus);
    if (legDestination) params.set('legDestination', legDestination);
    if (tripReference) params.set('tripReference', tripReference);
    params.set('manifestUiVersion', 'v2');

    const search = params.toString();
    const target = `/receive-manifest/${encodeURIComponent(manifestId)}${search ? `?${search}` : ''}`;

    return (
      <MuiLink
        component="button"
        type="button"
        underline="hover"
        onClick={() => {
          if (onCloseForNavigation) {
            onCloseForNavigation();
          } else {
            onClose();
          }
          navigate(target, {
            state: {
              tripId: tripId || undefined,
              legStatus: legStatus || undefined,
              legDestination: legDestination || undefined,
              tripReference: tripReference || undefined
            }
          });
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          p: 0,
          border: 0,
          bgcolor: 'transparent',
          fontSize: '0.82rem',
          fontWeight: 700,
          lineHeight: 1.3,
          wordBreak: 'break-word',
          color: 'primary.main',
          textDecoration: 'underline',
          textUnderlineOffset: '2px',
          cursor: 'pointer',
          textAlign: 'left',
          '&:hover': {
            color: 'primary.dark'
          }
        }}>
        {manifestId}
      </MuiLink>
    );
  };

  const queuePendingUploadFiles = (incomingFiles: File[]) => {
    if (!incomingFiles.length) return;

    const normalizedFiles = normalizeAttachmentFiles(incomingFiles);
    const supportedFiles = normalizedFiles.filter((file) => isSupportedAttachment(file));

    if (supportedFiles.length !== normalizedFiles.length) {
      alert('Some files were skipped because the file type is not supported.', 'warning');
    }

    const validSizeFiles = supportedFiles.filter((file) => file.size <= QM_MAX_ATTACHMENT_SIZE_BYTES);
    if (validSizeFiles.length !== supportedFiles.length) {
      const maxSizeInMb = Math.round((QM_MAX_ATTACHMENT_SIZE_BYTES / (1024 * 1024)) * 10) / 10;
      alert(`Some files were skipped because they exceed ${maxSizeInMb}MB.`, 'warning');
    }

    if (!validSizeFiles.length) return;

    setPendingUploadFiles(validSizeFiles);
    setIsUploadConfirmOpen(true);
  };

  const handleAttachmentDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!canUploadAttachments || isUploadingAttachments) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleAttachmentDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (!canUploadAttachments || isUploadingAttachments) return;
    event.preventDefault();
    attachmentDragDepthRef.current += 1;
    setIsAttachmentDragActive(true);
  };

  const handleAttachmentDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (!canUploadAttachments || isUploadingAttachments) return;
    event.preventDefault();
    attachmentDragDepthRef.current = Math.max(0, attachmentDragDepthRef.current - 1);
    setIsAttachmentDragActive(attachmentDragDepthRef.current > 0);
  };

  const handleAttachmentDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    attachmentDragDepthRef.current = 0;
    setIsAttachmentDragActive(false);
    if (!canUploadAttachments || isUploadingAttachments) return;

    const files = Array.from(event.dataTransfer.files || []);
    queuePendingUploadFiles(files);
  };

  const handleAttachmentPaste = (event: ClipboardEvent<HTMLDivElement>) => {
    if (!canUploadAttachments || isUploadingAttachments) return;

    const clipboardFiles = Array.from(event.clipboardData.files || []);
    if (clipboardFiles.length > 0) {
      event.preventDefault();
      queuePendingUploadFiles(clipboardFiles);
      return;
    }

    const itemFiles = Array.from(event.clipboardData.items || [])
      .map((item) => item.getAsFile())
      .filter((file): file is File => !!file);

    if (itemFiles.length > 0) {
      event.preventDefault();
      queuePendingUploadFiles(itemFiles);
    }
  };

  const availableStatusOptions = useMemo(() => {
    if (selectedQm?.status === 'PENDING') {
      return statusOptions.filter((status) => status !== 'CREATED');
    }

    return statusOptions;
  }, [selectedQm?.status, statusOptions]);

  const handleSaveStatus = async () => {
    if (isSavingStatus) return;
    setIsSavingStatus(true);
    try {
      await saveQmStatusEdits();
    } finally {
      setIsSavingStatus(false);
    }
  };

  const handleSaveDetail = async () => {
    if (isSavingDetail) return;
    setIsSavingDetail(true);
    try {
      const saved = await saveQmDetailEdits();
      if (saved) {
        setIsEditDialogOpen(false);
      }
    } finally {
      setIsSavingDetail(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          maxHeight: { xs: '94vh', md: '90vh' }
        }
      }}>
      <Stack
        spacing={1.5}
        sx={{
          p: { xs: 1.25, md: 1.75 },
          bgcolor: '#fafbfc',
          minHeight: 0,
          height: '100%',
          overflow: 'hidden',
          '& .MuiButton-root': { fontSize: { xs: '0.76rem', md: '0.8rem' } }
        }}>
        <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0, overflow: { xs: 'auto', lg: 'hidden' } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack spacing={0.6}>
              {selectedQm ? (
                <Stack direction="row" spacing={1.25} alignItems="baseline" flexWrap="wrap" useFlexGap>
                  <Typography variant="h6">{`QM ${selectedQm.qmNumber}`}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    QM Type: {getQmTypeLabel(selectedQm.type, selectedQm.typeLabel)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Raised by: {selectedQm.createdBy || '-'}
                  </Typography>
                  {siteInboundCoordinatorAssignmentLabel ? (
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={`Assigned coordinator: ${siteInboundCoordinatorAssignmentLabel}`}
                      sx={{
                        height: 24,
                        maxWidth: { xs: '100%', md: 420 },
                        '& .MuiChip-label': {
                          px: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }
                      }}
                    />
                  ) : null}
                </Stack>
              ) : (
                <Typography variant="h6">QM Details</Typography>
              )}
            </Stack>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>

          {!selectedQm || isDetailsLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : (
            <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
              {showFilteredOutNotice ? (
                <Alert severity="info">
                  This QM no longer matches the current table filters, but you can continue reviewing it here.
                </Alert>
              ) : null}

              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={1.5}
                sx={{ flex: 1, minHeight: 0, overflow: { xs: 'visible', lg: 'hidden' } }}>
                <Stack spacing={1.25} sx={{ flex: 1.2, overflowY: { xs: 'visible', lg: 'auto' }, pr: { lg: 0.5 } }}>
                  <Paper variant="outlined" sx={sectionCardSx}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2">QM Details</Typography>
                      <StyledButton
                        size="small"
                        variant="outlined"
                        onClick={() => setIsEditDialogOpen(true)}
                        disabled={!canEditDetailFields}>
                        Edit QM Details
                      </StyledButton>
                    </Stack>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 1
                      }}>
                      {(hasSiteInboundWave2Context
                        ? [
                            { label: 'Reason For QM', value: selectedQm.reasonLabel || '-' },
                            { label: 'Trip', value: selectedQm.tripReference || '-' },
                            { label: 'Manifest', value: renderManifestValue(selectedQm) },
                            {
                              label: 'SSCC / ASN',
                              value:
                                selectedQm.sscc && selectedQm.asn
                                  ? `${selectedQm.sscc} / ${selectedQm.asn}`
                                  : selectedQm.sscc || selectedQm.asn || '-'
                            },
                            {
                              label: 'Purchase Orders',
                              value: renderPurchaseOrderValue(selectedQm.poNumber)
                            },
                            { label: 'Selected ASN Lines', value: asnLineDisplayValue },
                            { label: 'Items', value: itemIdDisplayValue },
                            { label: 'Item Location', value: itemLocationDisplayValue },
                            { label: 'Packages', value: packageCountDisplayValue },
                            { label: 'Package Types', value: packageTypeDisplayValue },
                            { label: 'Contact', value: selectedQm.contactName || '-' },
                            { label: 'Additional Details', value: selectedQm.otherInformation || '-' },
                            { label: 'Description / Comments', value: formatDescription(selectedQm.description) },
                            { label: 'Created', value: dayjs(selectedQm.createdAt).format('DD/MM/YYYY HH:mm') }
                          ]
                        : [
                            { label: 'Reason For QM', value: selectedQm.reasonLabel || '-' },
                            {
                              label: 'SSCC / ASN',
                              value:
                                selectedQm.sscc && selectedQm.asn
                                  ? `${selectedQm.sscc} / ${selectedQm.asn}`
                                  : selectedQm.sscc || selectedQm.asn || '-'
                            },
                            {
                              label: 'Purchase Order',
                              value: renderPurchaseOrderValue(selectedQm.poNumber)
                            },
                            { label: 'Purchase Order Line', value: poLineDisplayValue },
                            { label: 'PO Line ID', value: poLineIdDisplayValue },
                            { label: 'Item ID', value: itemIdDisplayValue },
                            { label: 'Supplier', value: selectedQm.supplierName || '-' },
                            { label: 'Contact', value: selectedQm.contactName || '-' },
                            { label: 'Additional Details', value: selectedQm.otherInformation || '-' },
                            { label: 'Item Location', value: itemLocationDisplayValue },
                            { label: 'Number of Packages', value: packageCountDisplayValue },
                            { label: 'Package Types', value: packageTypeDisplayValue },
                            { label: 'Description / Comments', value: formatDescription(selectedQm.description) },
                            { label: 'Created', value: dayjs(selectedQm.createdAt).format('DD/MM/YYYY HH:mm') }
                          ]
                      )
                        .concat(
                          shouldShowClosureFields
                            ? [
                                {
                                  label: 'Closure Code',
                                  value: selectedQm.closureLabel || selectedQm.closureCode || '-'
                                },
                                { label: 'Closure Notes', value: selectedQm.closureNotes || '-' }
                              ]
                            : []
                        )
                        .map((item) => (
                          <Box
                            key={item.label}
                            sx={{
                              px: 1,
                              py: 0.85,
                              borderRadius: 1.25,
                              border: '1px solid',
                              borderColor: 'divider',
                              backgroundColor: 'background.default'
                            }}>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: 'text.secondary', mb: 0.2 }}>
                              {item.label}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.82rem',
                                color: 'text.primary',
                                lineHeight: 1.3,
                                whiteSpace: 'pre-line'
                              }}>
                              {item.value}
                            </Typography>
                          </Box>
                        ))}
                    </Box>
                  </Paper>

                  <Paper variant="outlined" sx={sectionCardSx}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2">Attachments</Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label={`${attachments.length} files`} variant="outlined" />
                      </Stack>
                    </Stack>
                    {canUploadAttachments ? (
                      <Box
                        onDragEnter={handleAttachmentDragEnter}
                        onDragOver={handleAttachmentDragOver}
                        onDragLeave={handleAttachmentDragLeave}
                        onDrop={handleAttachmentDrop}
                        onPaste={handleAttachmentPaste}
                        tabIndex={0}
                        sx={{
                          border: '2px dashed',
                          borderColor: isAttachmentDragActive ? 'primary.main' : 'primary.light',
                          borderRadius: 2,
                          p: 1.25,
                          mb: 1,
                          bgcolor: isAttachmentDragActive
                            ? alpha(theme.palette.primary.main, 0.12)
                            : alpha(theme.palette.primary.main, 0.04),
                          boxShadow: isAttachmentDragActive
                            ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.24)}, 0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`
                            : `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 160ms ease',
                          outline: 'none',
                          '&:focus-visible': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.28)}`
                          }
                        }}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ xs: 'stretch', sm: 'center' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            component="label"
                            sx={{ minWidth: 130, width: { xs: '100%', sm: 'auto' } }}
                            disabled={isUploadingAttachments}>
                            {isUploadingAttachments ? 'Uploading...' : 'Upload Files'}
                            <input
                              hidden
                              type="file"
                              multiple
                              accept={ATTACHMENT_ACCEPT_TYPES.join(',')}
                              onChange={(event) => {
                                const files = Array.from(event.target.files || []);
                                queuePendingUploadFiles(files);
                                event.currentTarget.value = '';
                              }}
                            />
                          </Button>
                          <Typography
                            sx={{
                              fontSize: '0.7rem',
                              color: isAttachmentDragActive ? 'primary.main' : 'text.secondary'
                            }}>
                            Drag and drop files here, or click here first then paste a screenshot with Ctrl+V.
                          </Typography>
                        </Stack>
                      </Box>
                    ) : null}
                    {isAttachmentsLoading ? (
                      <Stack alignItems="center" justifyContent="center" sx={{ py: 3 }}>
                        <CircularProgress size={22} />
                      </Stack>
                    ) : attachments.length > 0 ? (
                      <Stack spacing={1.25}>
                        {imageAttachments.length > 0 ? (
                          <Stack direction="row" flexWrap="wrap" gap={1.1}>
                            {imageAttachments.map((attachment) => (
                              <Box
                                key={attachment.id}
                                onClick={() =>
                                  onSelectPreview({ name: attachment.fileName, url: attachment.downloadUrl })
                                }
                                sx={{
                                  width: { xs: 90, md: 104 },
                                  height: { xs: 90, md: 104 },
                                  borderRadius: 1.5,
                                  overflow: 'hidden',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    borderColor: 'primary.main',
                                    transform: 'translateY(-1px)',
                                    boxShadow: 2
                                  }
                                }}>
                                <Box
                                  component="img"
                                  src={attachment.downloadUrl}
                                  alt={attachment.fileName}
                                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              </Box>
                            ))}
                          </Stack>
                        ) : null}

                        {fileAttachments.length > 0 ? (
                          <Stack spacing={0.75}>
                            {fileAttachments.map((attachment) => (
                              <Stack
                                key={attachment.id}
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{
                                  px: 1,
                                  py: 0.75,
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default'
                                }}>
                                <Stack spacing={0.2} sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" noWrap title={attachment.fileName}>
                                    {attachment.fileName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {formatAttachmentSize(attachment.sizeBytes)}
                                  </Typography>
                                </Stack>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => void downloadAttachmentWithOriginalFileName(attachment)}>
                                  Download
                                </Button>
                              </Stack>
                            ))}
                          </Stack>
                        ) : null}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No attachments found for this QM.
                      </Typography>
                    )}
                  </Paper>
                </Stack>

                <Stack spacing={1.25} sx={{ flex: 1, overflowY: { xs: 'visible', lg: 'auto' } }}>
                  <Paper variant="outlined" sx={sectionCardSx}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Coordinator Actions
                    </Typography>
                    <Stack
                      spacing={1}
                      sx={{
                        '& .MuiInputBase-input': { fontSize: '0.86rem' },
                        '& .MuiInputLabel-root': { fontSize: '0.86rem' },
                        '& .MuiFormHelperText-root': { fontSize: '0.72rem' }
                      }}>
                      <TextField
                        size="small"
                        label="Action Comment"
                        value={actionComment}
                        onChange={(event) => onActionCommentChange(event.target.value)}
                        multiline
                        rows={2}
                        disabled={!canEdit}
                      />
                      <StyledButton
                        variant="contained"
                        onClick={addActionComment}
                        sx={{ flex: 1 }}
                        disabled={!canEdit || !actionComment.trim() || isAddingActionComment}>
                        {isAddingActionComment ? 'Adding Action History...' : 'Add Action History'}
                      </StyledButton>

                      <Divider />

                      <TextField
                        size="small"
                        select
                        label="Status"
                        value={editStatus}
                        onChange={(event) => onEditStatusChange(event.target.value as QmCrudStatus)}
                        disabled={!canEditWorkflowFields}>
                        {availableStatusOptions.map((status) => (
                          <MenuItem key={status} value={status}>
                            {formatStatusLabel(status)}
                          </MenuItem>
                        ))}
                      </TextField>

                      {shouldShowClosureFields ? (
                        <>
                          <TextField
                            size="small"
                            select
                            label="Closure Code"
                            value={editClosureCode}
                            onChange={(event) => onEditClosureCodeChange(event.target.value)}
                            disabled={!canEditClosureFields}
                            required={canEditClosureFields}
                            error={canEditClosureFields && !editClosureCode}>
                            {closureOptions.map((item) => (
                              <MenuItem key={item.code} value={item.code}>
                                {item.label}
                              </MenuItem>
                            ))}
                          </TextField>

                          <TextField
                            size="small"
                            label="Closure Notes"
                            value={editClosureNotes}
                            onChange={(event) => onEditClosureNotesChange(event.target.value)}
                            multiline
                            rows={2}
                            disabled={!canEditClosureFields}
                          />
                        </>
                      ) : null}

                      <StyledButton
                        variant="contained"
                        onClick={handleSaveStatus}
                        disabled={
                          !canEditWorkflowFields ||
                          isClosedRecord ||
                          isSavingStatus ||
                          (canEditClosureFields && !editClosureCode)
                        }>
                        {isSavingStatus ? 'Saving QM Changes...' : 'Save QM Changes'}
                      </StyledButton>
                    </Stack>
                  </Paper>

                  <QmHistoryTimeline
                    selectedQm={selectedQm}
                    reasonOptions={reasonOptions}
                    closureOptions={closureOptions}
                    sectionCardSx={sectionCardSx}
                    getLocationLabel={getLocationLabel}
                    formatStatusLabel={formatStatusLabel}
                  />
                </Stack>
              </Stack>
            </Stack>
          )}
        </Stack>
        {selectedQm && !isDetailsLoading ? (
          <>
            <Divider />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1}
              sx={{ pt: 0.5 }}>
              {receivingWarehouseFooterLabel ? (
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 700 }}>
                    Receiving Warehouse
                  </Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: 'text.primary' }}>
                    {receivingWarehouseFooterLabel}
                  </Typography>
                </Box>
              ) : (
                <Box />
              )}
              <Stack direction="row" justifyContent="flex-end" spacing={1}>
                {onOpenInQmCases ? (
                  <StyledButton variant="contained" onClick={onOpenInQmCases}>
                    Open in QM Cases
                  </StyledButton>
                ) : null}
                <StyledButton
                  variant="outlined"
                  onClick={onReprintLabels}
                  disabled={!canReprintLabels || isReprintingLabels || Number(selectedQm.packageCount || 0) <= 0}>
                  {isReprintingLabels ? 'Reprinting Labels...' : 'Reprint Labels'}
                </StyledButton>
                <StyledButton variant="outlined" onClick={onClose}>
                  Close
                </StyledButton>
              </Stack>
            </Stack>
          </>
        ) : null}
      </Stack>

      <Dialog open={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)} fullWidth maxWidth="sm">
        <Stack
          spacing={1.5}
          sx={{
            p: 2.5,
            '& .MuiButton-root': { fontSize: { xs: '0.76rem', md: '0.8rem' } }
          }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>Edit QM Details</Typography>
          <Stack
            spacing={1.5}
            sx={{
              py: 0.5,
              '& .MuiInputBase-input': { fontSize: '0.86rem' },
              '& .MuiInputLabel-root': { fontSize: '0.86rem' },
              '& .MuiFormHelperText-root': { fontSize: '0.72rem' }
            }}>
            <TextField
              size="small"
              select
              label="Reason For QM"
              value={editReasonCode}
              onChange={(event) => onEditReasonCodeChange(event.target.value)}
              disabled={!canEditReason}>
              {filteredEditReasonOptions.map((item) => (
                <MenuItem key={item.code} value={item.code}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="Supplier"
              value={editSupplierName}
              onChange={(event) => onEditSupplierNameChange(event.target.value)}
              disabled={!canEditDetailFields}
            />

            <TextField
              size="small"
              label="Contact"
              value={editContactName}
              onChange={(event) => onEditContactNameChange(event.target.value)}
              disabled={!canEditDetailFields}
            />

            <TextField
              size="small"
              label="Additional Details"
              value={editOtherInformation}
              onChange={(event) => onEditOtherInformationChange(event.target.value)}
              multiline
              rows={2}
              disabled={!canEditDetailFields}
            />

            {selectedQm?.type === 'SITE_INBOUND' ? (
              <TextField
                size="small"
                select
                label="Site"
                value={editSiteInboundSiteId}
                onChange={(event) => onEditSiteInboundSiteIdChange?.(event.target.value)}
                disabled={!canEditItemLocation}>
                {siteInboundSiteOptions.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}

            <TextField
              size="small"
              select
              label="Item Location"
              value={editItemLocation}
              onChange={(event) => onEditItemLocationChange(event.target.value)}
              disabled={!canEditItemLocation || (selectedQm?.type === 'SITE_INBOUND' && !editSiteInboundSiteId)}>
              {locationSelectOptions.map((item) => (
                <MenuItem key={item.code} value={item.code}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="Description / Comments"
              value={editDescription}
              onChange={(event) => onEditDescriptionChange(event.target.value)}
              multiline
              rows={2}
              disabled={!canEditDetailFields}
            />
          </Stack>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <StyledButton variant="outlined" onClick={() => setIsEditDialogOpen(false)} disabled={isSavingDetail}>
              Cancel
            </StyledButton>
            <StyledButton
              variant="contained"
              onClick={handleSaveDetail}
              disabled={!canEditDetailFields || isClosedRecord || isSavingDetail}>
              {isSavingDetail ? 'Saving QM Changes...' : 'Save QM Changes'}
            </StyledButton>
          </Stack>
        </Stack>
      </Dialog>

      <Dialog
        open={isUploadConfirmOpen}
        onClose={() => {
          setIsUploadConfirmOpen(false);
          setPendingUploadFiles([]);
        }}
        maxWidth="xs"
        fullWidth>
        <Stack spacing={1.25} sx={{ p: 2, '& .MuiButton-root': { fontSize: { xs: '0.76rem', md: '0.8rem' } } }}>
          <Typography sx={{ fontSize: '0.96rem', fontWeight: 700 }}>Confirm Upload</Typography>
          <Typography sx={{ fontSize: '0.84rem', color: 'text.secondary' }}>
            Are you sure you want to upload {pendingUploadFiles.length} file(s)?
          </Typography>
          {pendingUploadFiles.length > 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 1,
                maxHeight: 180,
                overflowY: 'auto',
                backgroundColor: 'background.default'
              }}>
              <Stack spacing={0.6}>
                {pendingUploadFiles.map((file) => (
                  <Stack key={`${file.name}-${file.size}-${file.lastModified}`} direction="row" spacing={1}>
                    <Typography sx={{ fontSize: '0.78rem', color: 'text.primary', flex: 1 }}>{file.name}</Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {formatAttachmentSize(file.size)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          ) : null}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <StyledButton
              variant="outlined"
              onClick={() => {
                setIsUploadConfirmOpen(false);
                setPendingUploadFiles([]);
              }}>
              Cancel
            </StyledButton>
            <StyledButton
              variant="contained"
              onClick={() => {
                const files = pendingUploadFiles;
                setIsUploadConfirmOpen(false);
                setPendingUploadFiles([]);
                onUploadAttachments(files);
              }}
              disabled={!pendingUploadFiles.length || isUploadingAttachments}>
              Upload
            </StyledButton>
          </Stack>
        </Stack>
      </Dialog>
    </Dialog>
  );
}
